import { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, themeQuartz, type ColDef, type Theme } from 'ag-grid-community';
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise';
import { GridShell } from './CommonAgGrid.styled';
import type { CommonAgGridProps } from './types';

LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_LICENSE ?? '');

ModuleRegistry.registerModules([AllEnterpriseModule]);

/**
 * Project-branded AG Grid theme (BombyxPLM tokens) built with the v35 Theming API.
 * Exported so consumers can `.withParams()` further or reuse it directly.
 */
export const bombyxAgTheme: Theme = themeQuartz.withParams({
  fontFamily: "'Aktiv Grotesk', system-ui, sans-serif",
  fontSize: 13,
  headerHeight: 40,
  rowHeight: 40,
  headerFontWeight: 700,
  headerTextColor: '#000000',
  backgroundColor: '#ffffff',
  foregroundColor: '#000000',
  borderColor: 'rgba(106, 106, 106, 0.28)',
  borderRadius: 2,
  wrapperBorderRadius: 2,
  accentColor: '#159e79',
  selectedRowBackgroundColor: 'rgba(12, 151, 238, 0.08)',
  rowHoverColor: 'rgba(12, 151, 238, 0.05)',
  cellHorizontalPadding: 8,
});

/**
 * Generic, reusable AG Grid wrapper. Themed to BombyxPLM and pre-registered with
 * community modules — drop it anywhere and pass standard AG Grid props.
 */
function CommonAgGrid<TData = unknown>(props: CommonAgGridProps<TData>) {
  const { height, gridRef, className, theme, defaultColDef, ...rest } = props;

  const mergedDefaultColDef = useMemo<ColDef<TData>>(
    () => ({
      sortable: false,
      resizable: true,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
      ...defaultColDef,
    }),
    [defaultColDef],
  );

  return (
    <GridShell className={className} $height={height ?? '600px'}>
      <AgGridReact<TData>
        ref={gridRef}
        theme={theme ?? bombyxAgTheme}
        defaultColDef={mergedDefaultColDef}
        {...rest}
      />
    </GridShell>
  );
}

export default CommonAgGrid;
